import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Table, TableBody, TableCell, TableRow, Divider, IconButton } from '@mui/material';
import { Close as CloseIcon, Print as PrintIcon } from '@mui/icons-material';
interface Transaction {
  id: string;
  senderName: string;
  receiverName: string;
  amount: number;
  currency: string;
  payoutAmount: number;
  payoutCurrency: string;
  date: string;
  status: string;
  mto: string;
  branchCode: string;
}
interface ReceiptGeneratorProps {
  transaction: Transaction;
  receiptType: 'ePRC' | 'MoneyGram' | 'BankReceipt';
  isOpen: boolean;
  onClose: () => void;
}
const ReceiptGenerator: React.FC<ReceiptGeneratorProps> = ({
  transaction,
  receiptType,
  isOpen,
  onClose
}) => {
  const handlePrint = () => {
    window.print();
  };
  const renderEPRCReceipt = () => {
    const today = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const transactionDate = new Date(transaction.date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    return <Box sx={{
      bgcolor: 'white',
      p: 4,
      '@media print': {
        p: 2
      }
    }} data-id="element-1958">
        {/* Header */}
        <Box sx={{
        textAlign: 'center',
        mb: 3
      }} data-id="element-1959">
          <Typography variant="h6" sx={{
          fontWeight: 600,
          mb: 0.5
        }} data-id="element-1960">
            Electronic-Proceeds Realization Certificate (e-PRC)
          </Typography>
          <Typography variant="body2" color="text.secondary" data-id="element-1961">
            Personal / Corporate Customer
          </Typography>
        </Box>

        {/* e-PRC Details */}
        <Table size="small" sx={{
        mb: 3
      }} data-id="element-1962">
          <TableBody data-id="element-1963">
            <TableRow data-id="element-1964">
              <TableCell sx={{
              fontWeight: 600,
              width: '40%',
              border: '1px solid #ddd'
            }} data-id="element-1965">
                e-PRC No. :
              </TableCell>
              <TableCell sx={{
              border: '1px solid #ddd'
            }} data-id="element-1966">
                BIPL-IME-{transaction.id.replace('TXN', '')}
              </TableCell>
            </TableRow>
            <TableRow data-id="element-1967">
              <TableCell sx={{
              fontWeight: 600,
              border: '1px solid #ddd'
            }} data-id="element-1968">
                Date of e-PRC :
              </TableCell>
              <TableCell sx={{
              border: '1px solid #ddd'
            }} data-id="element-1969">
                {today}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Remitter and Beneficiary Details */}
        <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 3
      }} data-id="element-1970">
          {/* Remitter's Detail */}
          <Box sx={{
          flex: 1
        }} data-id="element-1971">
            <Typography variant="subtitle2" sx={{
            fontWeight: 600,
            mb: 1,
            bgcolor: '#f5f5f5',
            p: 1
          }} data-id="element-1972">
              Remitter's Detail:
            </Typography>
            <Table size="small" data-id="element-1973">
              <TableBody data-id="element-1974">
                <TableRow data-id="element-1975">
                  <TableCell sx={{
                  fontWeight: 600,
                  border: '1px solid #ddd',
                  width: '50%'
                }} data-id="element-1976">
                    Name of Person/ Entity
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd',
                  textTransform: 'uppercase'
                }} data-id="element-1977">
                    {transaction.senderName}
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-1978">
                  <TableCell sx={{
                  fontWeight: 600,
                  border: '1px solid #ddd'
                }} data-id="element-1979">
                    NICOP/ Passport No./ CNIC/ POC/ Entity Registration No./ Any
                    other Unique ID
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd'
                }} data-id="element-1980">
                    N/A
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-1981">
                  <TableCell sx={{
                  fontWeight: 600,
                  border: '1px solid #ddd'
                }} data-id="element-1982">
                    IBAN / Bank Account No. (if applicable)
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd'
                }} data-id="element-1983">
                    N/A
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-1984">
                  <TableCell sx={{
                  fontWeight: 600,
                  border: '1px solid #ddd'
                }} data-id="element-1985">
                    Name of Remitting (originating) Financial Institution
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd'
                }} data-id="element-1986">
                    {transaction.mto}
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-1987">
                  <TableCell sx={{
                  fontWeight: 600,
                  border: '1px solid #ddd'
                }} data-id="element-1988">
                    Name of Country
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd'
                }} data-id="element-1989">
                    {transaction.country || 'United Kingdom'}
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-1990">
                  <TableCell sx={{
                  fontWeight: 600,
                  border: '1px solid #ddd'
                }} data-id="element-1991">
                    Country Code (as per ITRS)
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd'
                }} data-id="element-1992">
                    GBR
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {/* Beneficiary's Detail */}
          <Box sx={{
          flex: 1
        }} data-id="element-1993">
            <Typography variant="subtitle2" sx={{
            fontWeight: 600,
            mb: 1,
            bgcolor: '#f5f5f5',
            p: 1
          }} data-id="element-1994">
              Beneficiary's Detail:
            </Typography>
            <Table size="small" data-id="element-1995">
              <TableBody data-id="element-1996">
                <TableRow data-id="element-1997">
                  <TableCell sx={{
                  fontWeight: 600,
                  border: '1px solid #ddd',
                  width: '50%'
                }} data-id="element-1998">
                    Name of Person/ Entity
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd',
                  textTransform: 'uppercase'
                }} data-id="element-1999">
                    {transaction.receiverName}
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-2000">
                  <TableCell sx={{
                  fontWeight: 600,
                  border: '1px solid #ddd'
                }} data-id="element-2001">
                    NICOP/ CNIC/ Passport No./ NTN
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd'
                }} data-id="element-2002">
                    N/A
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-2003">
                  <TableCell sx={{
                  fontWeight: 600,
                  border: '1px solid #ddd'
                }} data-id="element-2004">
                    Name of the Bank
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd'
                }} data-id="element-2005">
                    BankIslami Pakistan Limited
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-2006">
                  <TableCell sx={{
                  fontWeight: 600,
                  border: '1px solid #ddd'
                }} data-id="element-2007">
                    IBAN / Bank Account No.
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd'
                }} data-id="element-2008">
                    N/A
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Certification Text */}
        <Typography variant="body2" sx={{
        mb: 2,
        textAlign: 'justify'
      }} data-id="element-2009">
          This is to certify that we have received remittance by the order of
          the above mentioned Remitter and have paid the equivalent Pak Rupees
          to the Beneficiary, as per the detail given as below:
        </Typography>

        {/* Transaction Detail */}
        <Typography variant="subtitle2" sx={{
        fontWeight: 600,
        mb: 1,
        bgcolor: '#f5f5f5',
        p: 1
      }} data-id="element-2010">
          Transaction Detail:
        </Typography>
        <Table size="small" sx={{
        mb: 3
      }} data-id="element-2011">
          <TableBody data-id="element-2012">
            <TableRow data-id="element-2013">
              <TableCell sx={{
              fontWeight: 600,
              border: '1px solid #ddd',
              width: '50%'
            }} data-id="element-2014">
                Date of Realization of Proceeds
              </TableCell>
              <TableCell sx={{
              border: '1px solid #ddd'
            }} data-id="element-2015">
                {transactionDate}
              </TableCell>
            </TableRow>
            <TableRow data-id="element-2016">
              <TableCell sx={{
              fontWeight: 600,
              border: '1px solid #ddd'
            }} data-id="element-2017">
                Amount of Remittance (in FCY)*
              </TableCell>
              <TableCell sx={{
              border: '1px solid #ddd'
            }} data-id="element-2018">
                {transaction.amount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}{' '}
                {transaction.currency}
              </TableCell>
            </TableRow>
            <TableRow data-id="element-2019">
              <TableCell sx={{
              fontWeight: 600,
              border: '1px solid #ddd'
            }} data-id="element-2020">
                Rate of Conversion*
              </TableCell>
              <TableCell sx={{
              border: '1px solid #ddd'
            }} data-id="element-2021">
                {(transaction.payoutAmount / transaction.amount).toFixed(2)}
              </TableCell>
            </TableRow>
            <TableRow data-id="element-2022">
              <TableCell sx={{
              fontWeight: 600,
              border: '1px solid #ddd'
            }} data-id="element-2023">
                Amount of Remittance (in PKR)
              </TableCell>
              <TableCell sx={{
              border: '1px solid #ddd'
            }} data-id="element-2024">
                {transaction.payoutAmount.toLocaleString('en-PK', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </TableCell>
            </TableRow>
            <TableRow data-id="element-2025">
              <TableCell sx={{
              fontWeight: 600,
              border: '1px solid #ddd'
            }} data-id="element-2026">
                Purpose Code (as per ITRS)
              </TableCell>
              <TableCell sx={{
              border: '1px solid #ddd'
            }} data-id="element-2027">
                9471
              </TableCell>
            </TableRow>
            <TableRow data-id="element-2028">
              <TableCell sx={{
              fontWeight: 600,
              border: '1px solid #ddd'
            }} data-id="element-2029">
                Purpose of Remittance
              </TableCell>
              <TableCell sx={{
              border: '1px solid #ddd'
            }} data-id="element-2030">
                Family Assistance
              </TableCell>
            </TableRow>
            <TableRow data-id="element-2031">
              <TableCell sx={{
              fontWeight: 600,
              border: '1px solid #ddd'
            }} data-id="element-2032">
                Transaction Reference No.
              </TableCell>
              <TableCell sx={{
              border: '1px solid #ddd'
            }} data-id="element-2033">
                {transaction.id}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Footer Notes */}
        <Box sx={{
        fontSize: '0.75rem',
        color: 'text.secondary'
      }} data-id="element-2034">
          <Typography variant="caption" display="block" sx={{
          mb: 0.5
        }} data-id="element-2035">
            [ The above transaction has been will be reported to SBP/ SBP-BSC in
            Schedule 54 / 03 of ITRS for the period : Nov 2025 (if applicable) ]
          </Typography>
          <Typography variant="caption" display="block" sx={{
          mb: 0.5
        }} data-id="element-2036">
            Note: This is a system-generated document. No signature is required.
          </Typography>
          <Typography variant="caption" display="block" data-id="element-2037">
            * N/A in case the payment is received through Vostro account of the
            remitting institution, and the rate of conversion has been applied
            by the remitting institution abroad.
          </Typography>
        </Box>
      </Box>;
  };
  const renderMoneyGramReceipt = () => {
    const receiverFirstName = transaction.receiverName.split(' ')[0] || '';
    const receiverLastName = transaction.receiverName.split(' ').slice(1).join(' ') || '';
    const senderFirstName = transaction.senderName.split(' ')[0] || '';
    const senderLastName = transaction.senderName.split(' ').slice(1).join(' ') || '';
    return <Box sx={{
      bgcolor: 'white',
      p: 4,
      '@media print': {
        p: 2
      }
    }} data-id="element-2038">
        {/* Header */}
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-2039">
          <Box data-id="element-2040">
            <Typography variant="h5" sx={{
            fontWeight: 600,
            mb: 0.5
          }} data-id="element-2041">
              Remittance Payment Receipt
            </Typography>
            <Typography variant="body2" sx={{
            fontWeight: 600
          }} data-id="element-2042">
              Receive Form
            </Typography>
            <Typography variant="caption" color="text.secondary" data-id="element-2043">
              PLEASE FILL IN BLOCK CAPITALS
            </Typography>
            <Typography variant="body2" sx={{
            mt: 1
          }} data-id="element-2044">
              REFERENCE NO: {transaction.id}
            </Typography>
          </Box>
          <Box sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center'
        }} data-id="element-2045">
            <Box component="img" src="https://cdn.magicpatterns.com/uploads/cfFBsKjFJe7LCZDcdRZyJg/bank-islami-logo.png" alt="BankIslami" sx={{
            height: 50
          }} data-id="element-2046" />
            <Box component="img" src="https://cdn.magicpatterns.com/uploads/vW5s534aagcApjbRC331jh/mglogo.png" alt="MoneyGram" sx={{
            height: 50
          }} data-id="element-2047" />
          </Box>
          <Box sx={{
          border: '2px solid black',
          px: 2,
          py: 1,
          fontWeight: 600
        }} data-id="element-2048">
            Duplicate
          </Box>
        </Box>

        <Divider sx={{
        mb: 3,
        borderColor: 'black',
        borderWidth: 2
      }} data-id="element-2049" />

        {/* Receiver Section */}
        <Box sx={{
        border: '2px solid #e74c3c',
        p: 2,
        mb: 2
      }} data-id="element-2050">
          <Typography variant="subtitle2" sx={{
          fontWeight: 600,
          mb: 2
        }} data-id="element-2051">
            RECEIVER
          </Typography>
          <Table size="small" data-id="element-2052">
            <TableBody data-id="element-2053">
              <TableRow data-id="element-2054">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                width: '30%'
              }} data-id="element-2055">
                  First Name
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd',
                textTransform: 'uppercase'
              }} data-id="element-2056">
                  {receiverFirstName}
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2057">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2058">
                  Middle Name
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2059"></TableCell>
              </TableRow>
              <TableRow data-id="element-2060">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2061">
                  Last Name
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd',
                textTransform: 'uppercase'
              }} data-id="element-2062">
                  {receiverLastName}
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2063">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2064">
                  Address
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd',
                textTransform: 'uppercase'
              }} data-id="element-2065">
                  {transaction.branchCode ? `Branch Code: ${transaction.branchCode}` : 'N/A'}
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2066">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2067">
                  Town/City
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2068">
                  {transaction.country || 'Pakistan'}
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2069">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2070">
                  Postal Code
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2071">
                  N/A
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2072">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2073">
                  Country
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2074">
                  PAK
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2075">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2076">
                  Contact No
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2077">
                  N/A
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Sender Section */}
        <Box sx={{
        border: '2px solid #e74c3c',
        p: 2,
        mb: 2
      }} data-id="element-2078">
          <Typography variant="subtitle2" sx={{
          fontWeight: 600,
          mb: 2
        }} data-id="element-2079">
            SENDER
          </Typography>
          <Table size="small" data-id="element-2080">
            <TableBody data-id="element-2081">
              <TableRow data-id="element-2082">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                width: '30%'
              }} data-id="element-2083">
                  First Name
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd',
                textTransform: 'uppercase'
              }} data-id="element-2084">
                  {senderFirstName}
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2085">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2086">
                  Middle Name
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2087"></TableCell>
              </TableRow>
              <TableRow data-id="element-2088">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2089">
                  Last Name
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd',
                textTransform: 'uppercase'
              }} data-id="element-2090">
                  {senderLastName}
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2091">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2092">
                  Purpose of Transaction
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2093">
                  Family Support
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Signature Section */}
        <Box sx={{
        border: '2px solid #e74c3c',
        p: 2,
        mb: 2
      }} data-id="element-2094">
          <Typography variant="subtitle2" sx={{
          fontWeight: 600,
          mb: 2
        }} data-id="element-2095">
            SIGNATURE
          </Typography>
          <Typography variant="body2" sx={{
          mb: 2,
          fontSize: '0.85rem'
        }} data-id="element-2096">
            This is our standard form client agreement upon which we intend to
            rely. For your own benefits and protection, you should read the
            terms and conditions which apply to your transaction carefully
            before signing below if you do not understand any point please ask
            for future information. I acknowledge receipt of the MoneyGram
            transfer as provided above
          </Typography>
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 4
        }} data-id="element-2097">
            <Box data-id="element-2098">
              <Divider sx={{
              width: 200,
              mb: 0.5
            }} data-id="element-2099" />
              <Typography variant="caption" data-id="element-2100">Receiver Signature:</Typography>
            </Box>
            <Box data-id="element-2101">
              <Divider sx={{
              width: 200,
              mb: 0.5
            }} data-id="element-2102" />
              <Typography variant="caption" data-id="element-2103">Date</Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{
          display: 'block',
          mt: 2,
          textAlign: 'center'
        }} data-id="element-2104">
            For Our Complaints handling procedure, Please see Below
          </Typography>
          <Typography variant="caption" sx={{
          display: 'block',
          textAlign: 'center',
          fontWeight: 600
        }} data-id="element-2105">
            DISTRIBUTION: TOP COPY AGENT BOTTOM COPY CUSTOMER
          </Typography>
        </Box>

        {/* Agent Use Only Section */}
        <Box sx={{
        border: '2px solid #e74c3c',
        p: 2
      }} data-id="element-2106">
          <Typography variant="subtitle2" sx={{
          fontWeight: 600,
          mb: 2
        }} data-id="element-2107">
            AGENT USE ONLY
          </Typography>
          <Box sx={{
          display: 'flex',
          gap: 4
        }} data-id="element-2108">
            <Box sx={{
            flex: 1
          }} data-id="element-2109">
              <Table size="small" data-id="element-2110">
                <TableBody data-id="element-2111">
                  <TableRow data-id="element-2112">
                    <TableCell sx={{
                    border: '1px solid #ddd',
                    fontWeight: 600
                  }} data-id="element-2113">
                      Authorization Code
                    </TableCell>
                    <TableCell sx={{
                    border: '1px solid #ddd'
                  }} data-id="element-2114"></TableCell>
                  </TableRow>
                  <TableRow data-id="element-2115">
                    <TableCell sx={{
                    border: '1px solid #ddd',
                    fontWeight: 600
                  }} data-id="element-2116">
                      MoneyGram Operator ID No. (For Vice Transaction Only)
                    </TableCell>
                    <TableCell sx={{
                    border: '1px solid #ddd'
                  }} data-id="element-2117"></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box sx={{
              mt: 4,
              textAlign: 'center'
            }} data-id="element-2118">
                <Divider sx={{
                mb: 0.5
              }} data-id="element-2119" />
                <Typography variant="caption" data-id="element-2120">Office Name/Stamp</Typography>
              </Box>
            </Box>
            <Box sx={{
            flex: 1
          }} data-id="element-2121">
              <Table size="small" data-id="element-2122">
                <TableBody data-id="element-2123">
                  <TableRow data-id="element-2124">
                    <TableCell sx={{
                    border: '1px solid #ddd',
                    fontWeight: 600
                  }} data-id="element-2125">
                      Amount Paid
                    </TableCell>
                    <TableCell sx={{
                    border: '1px solid #ddd'
                  }} data-id="element-2126"></TableCell>
                  </TableRow>
                  <TableRow data-id="element-2127">
                    <TableCell sx={{
                    border: '1px solid #ddd',
                    fontWeight: 600
                  }} data-id="element-2128">
                      Currency
                    </TableCell>
                    <TableCell sx={{
                    border: '1px solid #ddd'
                  }} data-id="element-2129">
                      <strong data-id="element-2130">{transaction.payoutCurrency}</strong>
                    </TableCell>
                  </TableRow>
                  <TableRow data-id="element-2131">
                    <TableCell sx={{
                    border: '1px solid #ddd',
                    fontWeight: 600
                  }} data-id="element-2132">
                      Amount
                    </TableCell>
                    <TableCell sx={{
                    border: '1px solid #ddd'
                  }} data-id="element-2133">
                      <strong data-id="element-2134">{transaction.payoutAmount.toFixed(2)}</strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Typography variant="caption" sx={{
              display: 'block',
              mt: 2
            }} data-id="element-2135">
                Was the Question used/answered Correctly? (please tick) ☐
              </Typography>
            </Box>
          </Box>

          {/* ID Verification */}
          <Box sx={{
          mt: 3,
          border: '1px solid #ddd',
          p: 2
        }} data-id="element-2136">
            <Typography variant="body2" sx={{
            fontWeight: 600,
            mb: 1
          }} data-id="element-2137">
              For transaction requiring Identification (ID): (as per the local
              regulation and/ or per MoneyGram's Policy : if the transaction is
              greater or equal is 900 USD or its equivalent in local currency)
            </Typography>
            <Table size="small" data-id="element-2138">
              <TableBody data-id="element-2139">
                <TableRow data-id="element-2140">
                  <TableCell sx={{
                  border: '1px solid #ddd',
                  fontWeight: 600,
                  width: '30%'
                }} data-id="element-2141">
                    Receiver:
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd',
                  textTransform: 'uppercase'
                }} data-id="element-2142">
                    {transaction.receiverName}
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-2143">
                  <TableCell sx={{
                  border: '1px solid #ddd',
                  fontWeight: 600
                }} data-id="element-2144">
                    Type Of ID (Not Expired):
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd'
                }} data-id="element-2145">
                    CNIC
                  </TableCell>
                </TableRow>
                <TableRow data-id="element-2146">
                  <TableCell sx={{
                  border: '1px solid #ddd',
                  fontWeight: 600
                }} data-id="element-2147">
                    ID Date of Expiry:
                  </TableCell>
                  <TableCell sx={{
                  border: '1px solid #ddd'
                }} data-id="element-2148">
                    30-11-2033
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Typography variant="caption" sx={{
            display: 'block',
            mt: 2
          }} data-id="element-2149">
              For transaction requiring Identification (ID): (as per the local
              regulation and/ or per MoneyGram's Policy : if the transaction is
              greater or equal is 300 USD or its equivalent in local currency)
            </Typography>
            <Typography variant="caption" sx={{
            display: 'block',
            mt: 1
          }} data-id="element-2150">
              If the Receiver is US Citizen:
            </Typography>
          </Box>
        </Box>
      </Box>;
  };
  const renderBankReceipt = () => {
    const transactionDate = new Date(transaction.date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const transactionTime = new Date(transaction.date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    // Get MTO logo based on transaction MTO
    const getMTOLogo = () => {
      const mtoLower = transaction.mto.toLowerCase();
      if (mtoLower.includes('ria')) {
        return 'https://cdn.magicpatterns.com/uploads/3p9CwMgh4ndFsWDMagV7cT/ria_logo.png';
      } else if (mtoLower.includes('ime') || mtoLower.includes('international money express')) {
        return 'https://cdn.magicpatterns.com/uploads/i8D3THf4Gp9A29dm9G3ZLN/ime_logo.png';
      } else if (mtoLower.includes('instantcash')) {
        return 'https://cdn.magicpatterns.com/uploads/mJPkmiuB3PwkidCTBMJLtw/instantcash_logo.jpg';
      } else if (mtoLower.includes('express money') || mtoLower.includes('xpress money')) {
        return 'https://cdn.magicpatterns.com/uploads/pJMwgcAoWDQLmFnPZiDDuu/xpress_money_logo.png';
      } else if (mtoLower.includes('jingle pay')) {
        return 'https://cdn.magicpatterns.com/uploads/cz9vZ95wJBkLgyxryR8a22/jingle_pay_logo.png';
      }
      // Default to IME logo if MTO not recognized
      return 'https://cdn.magicpatterns.com/uploads/i8D3THf4Gp9A29dm9G3ZLN/ime_logo.png';
    };
    return <Box sx={{
      bgcolor: 'white',
      p: 4,
      '@media print': {
        p: 2
      }
    }} data-id="element-2151">
        {/* Header */}
        <Box sx={{
        textAlign: 'center',
        mb: 3,
        position: 'relative'
      }} data-id="element-2152">
          <Typography variant="h5" sx={{
          fontWeight: 600,
          color: '#5a5a5a',
          mb: 1
        }} data-id="element-2153">
            Remittance Payment Receipt
          </Typography>
          <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          border: '2px solid #000',
          px: 2,
          py: 0.5,
          fontWeight: 600
        }} data-id="element-2154">
            Duplicate
          </Box>
        </Box>

        {/* Bank Logos */}
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        pb: 2,
        borderBottom: '2px solid #000'
      }} data-id="element-2155">
          <Box component="img" src="https://cdn.magicpatterns.com/uploads/cfFBsKjFJe7LCZDcdRZyJg/bank-islami-logo.png" alt="BankIslami" sx={{
          height: 60
        }} data-id="element-2156" />
          <Box component="img" src={getMTOLogo()} alt={transaction.mto} sx={{
          height: 50,
          maxWidth: 200,
          objectFit: 'contain'
        }} data-id="element-2157" />
        </Box>

        {/* Receiver Details */}
        <Box sx={{
        mb: 3
      }} data-id="element-2158">
          <Typography variant="h6" sx={{
          fontWeight: 600,
          mb: 2,
          color: '#333'
        }} data-id="element-2159">
            RECEIVER DETAILS
          </Typography>
          <Table size="small" data-id="element-2160">
            <TableBody data-id="element-2161">
              <TableRow data-id="element-2162">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                width: '40%',
                bgcolor: '#f5f5f5'
              }} data-id="element-2163">
                  Beneficiary Name
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2164">
                  {transaction.receiverName}
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2165">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2166">
                  Valid ID/CNIC
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2167">
                  34603-2497626-9
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2168">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2169">
                  Contact No
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2170">
                  0302-5823623
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2171">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2172">
                  PIN CODE
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2173">
                  95543036536
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2174">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2175">
                  Remittance Amount
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600
              }} data-id="element-2176">
                  {transaction.payoutAmount.toFixed(2)}
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2177">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2178">
                  Remittance Amount In Words
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2179">
                  {transaction.payoutAmount.toFixed(2)} PKR
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2180">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2181">
                  Address
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2182">
                  HAJI PURA SILAKOT
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2183">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2184">
                  Payment Date
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2185">
                  {transactionDate} {transactionTime}
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2186">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2187">
                  Country
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2188">
                  PK
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2189">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2190">
                  Branch Code
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2191">
                  {transaction.branchCode || '3077'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Sender Details */}
        <Box sx={{
        mb: 3
      }} data-id="element-2192">
          <Typography variant="h6" sx={{
          fontWeight: 600,
          mb: 2,
          color: '#333'
        }} data-id="element-2193">
            SENDER DETAILS
          </Typography>
          <Table size="small" data-id="element-2194">
            <TableBody data-id="element-2195">
              <TableRow data-id="element-2196">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                width: '40%',
                bgcolor: '#f5f5f5'
              }} data-id="element-2197">
                  Remitter Name
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2198">
                  {transaction.senderName}
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2199">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2200">
                  Contact No
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2201">
                  2679705701
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2202">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2203">
                  Valid ID
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2204">
                  N/A
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2205">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2206">
                  Purpose of Remittance
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2207">
                  Family Support
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2208">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2209">
                  Relation with Beneficiary
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2210">
                  Family
                </TableCell>
              </TableRow>
              <TableRow data-id="element-2211">
                <TableCell sx={{
                border: '1px solid #ddd',
                fontWeight: 600,
                bgcolor: '#f5f5f5'
              }} data-id="element-2212">
                  City
                </TableCell>
                <TableCell sx={{
                border: '1px solid #ddd'
              }} data-id="element-2213">
                  Philadelphia
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Signature Section */}
        <Box sx={{
        mb: 3,
        border: '1px solid #ddd',
        p: 2
      }} data-id="element-2214">
          <Typography variant="h6" sx={{
          fontWeight: 600,
          mb: 2,
          color: '#333'
        }} data-id="element-2215">
            SIGNATURE
          </Typography>
          <Typography variant="body2" sx={{
          mb: 3,
          fontSize: '0.85rem',
          color: '#666'
        }} data-id="element-2216">
            This is our standard form client agreement upon which we intend to
            rely. for your own benefits and protection, you should read the
            terms and conditions which apply to your transaction carefully
            before signing below if you do not understand any point please ask
            for future information. I acknowledge receipt of the{' '}
            {transaction.mto} transfer as provided above
          </Typography>
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 4
        }} data-id="element-2217">
            <Box sx={{
            width: '45%'
          }} data-id="element-2218">
              <Divider sx={{
              mb: 0.5,
              borderColor: '#000'
            }} data-id="element-2219" />
              <Typography variant="caption" data-id="element-2220">Receiver Signature:</Typography>
            </Box>
            <Box sx={{
            width: '45%'
          }} data-id="element-2221">
              <Divider sx={{
              mb: 0.5,
              borderColor: '#000'
            }} data-id="element-2222" />
              <Typography variant="caption" data-id="element-2223">Date:</Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{
          display: 'block',
          mt: 3,
          textAlign: 'center',
          fontSize: '0.75rem'
        }} data-id="element-2224">
            For Our Complaints handling procedure,Please see Below
          </Typography>
        </Box>
      </Box>;
  };
  return <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
    sx: {
      '@media print': {
        boxShadow: 'none',
        maxWidth: '100%',
        margin: 0
      }
    }
  }} data-id="element-2225">
      <DialogTitle sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      '@media print': {
        display: 'none'
      }
    }} data-id="element-2226">
        <Typography variant="h6" data-id="element-2227">
          {receiptType === 'ePRC' ? 'e-PRC Receipt' : receiptType === 'MoneyGram' ? 'MoneyGram Receipt' : 'Bank Receipt (IME)'}
        </Typography>
        <IconButton onClick={onClose} size="small" data-id="element-2228">
          <CloseIcon data-id="element-2229" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers data-id="element-2230">
        {receiptType === 'ePRC' ? renderEPRCReceipt() : receiptType === 'MoneyGram' ? renderMoneyGramReceipt() : renderBankReceipt()}
      </DialogContent>
      <DialogActions sx={{
      '@media print': {
        display: 'none'
      }
    }} data-id="element-2231">
        <Button onClick={onClose} data-id="element-2232">Close</Button>
        <Button variant="contained" startIcon={<PrintIcon data-id="element-2234" />} onClick={handlePrint} data-id="element-2233">
          Print Receipt
        </Button>
      </DialogActions>
    </Dialog>;
};
export default ReceiptGenerator;